import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { DOCUMENT_CATEGORIES } from '../types';
import { LibraryItem } from '../components/library-list/types';
import { useTranslation } from 'react-i18next';
import { selectAllCompanies } from '../../../slices/companies/companiesSlice';
import { NWClient } from '../../../client/NWClient';
import { fetchDocuments } from '../../../slices/documents/documentsSlice';
import { fetchDocumentTemplates } from '../../../slices/document-templates/documentTemplatesSlice';
import { fetchDisclaimerInfo } from '../../../slices/disclaimer/disclaimerSlice';
import { ROUTES } from '../../../common/constants/routes';
import { useLocation, useSearchParams } from 'react-router-dom';
import { fetchEmployees } from '../../../slices/employees/employeesSlice';
import { fetchCustomers } from '../../../slices/customers/customersSlice';
import { fetchSuppliers } from '../../../slices/suppliers/suppliersSlice';
import { fetchBusinessProfileContactById } from '../../../slices/business-profile/businessProfileSlice';

export const useLibrary = () => {
  const currentUser = useAppSelector(selectUser);
  const company = useAppSelector(selectAllCompanies)[0];
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [documentLibraryItems, setDocumentLibraryItems] = useState<LibraryItem[]>([]);
  const [activeTab, setActiveTab] = useState<DOCUMENT_CATEGORIES>('All');
  const [libraryType, setLibraryType] = useState<null | 'businessKitz' | 'own'>(null);
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);
  const [region, setRegion] = useState<'uk' | 'au' | 'global'>('au');
  const [disclaimerMessage, setDisclaimerMessage] = useState<boolean>(false);
  const libraryItems = [...documentLibraryItems];
  const [params] = useSearchParams();
  const isBusinessKitzLibrary = params.get('businessKitzLibrary');
  const isOwnLibrary = params.get('ownLibrary');
  const handleTabChange = (tab: DOCUMENT_CATEGORIES) => {
    setActiveTab(tab);
  };

  const sortByDate = (a: any, b: any) => {
    return Number(new Date(b.created)) - Number(new Date(a.created));
  };

  const getDocuments = useCallback(() => {
    if (currentUser.id) {
      dispatch(
        fetchDocuments({
          data: {
            user: currentUser?.id,
          },
        })
      );
    }
  }, [dispatch, currentUser?.id]);

  const getDisclaimerInfo = useCallback(() => {
    dispatch(fetchDisclaimerInfo());
  }, [dispatch]);

  useEffect(() => {
    getDocuments();
  }, [getDocuments]);

  useEffect(() => {
    getDisclaimerInfo();
  }, [getDisclaimerInfo]);

  useEffect(() => {
    if (location.pathname === ROUTES.DOCUMENT_LIBRARY) {
      setLibraryType('businessKitz');
    }
  }, [location.pathname]);

  useEffect(() => {
    setPendingStatus(true);
    if (libraryType === 'businessKitz') {
      NWClient.list(
        'document-library',
        activeTab === 'All' ? { region } : { category: activeTab, region }
      )
        .then((res: LibraryItem[]) => {
          const templatesSortedByName = [...res].sort((a, b) => a.name.localeCompare(b.name));
          setDocumentLibraryItems(templatesSortedByName);
        })
        .finally(() => {
          setPendingStatus(false);
        });
    } else {
      dispatch(fetchDocumentTemplates({ data: { user: currentUser.id } }))
        .unwrap()
        .finally(() => {
          setPendingStatus(false);
        });
    }
  }, [activeTab, libraryType, region]);

  useEffect(() => {
    dispatch(fetchEmployees({ company_id: company.id }));
    dispatch(fetchCustomers({ company_id: company.id }));
    dispatch(fetchSuppliers({ company_id: company.id }));
    dispatch(fetchBusinessProfileContactById({ id: company.id }));
  }, []);

  useEffect(() => {
    if (isBusinessKitzLibrary) {
      setLibraryType('businessKitz');
    }
    if (isOwnLibrary) {
      setLibraryType('own');
    }
  }, [isBusinessKitzLibrary, isOwnLibrary]);

  return {
    libraryItems,
    activeTab,
    handleTabChange,
    libraryType,
    setLibraryType,
    pendingStatus,
    region,
    setRegion,
    disclaimerMessage,
    setDisclaimerMessage,
  };
};
